/**
 * Element utilities for tracking and selection
 */

/**
 * Generate a unique CSS selector for an element
 */
export function getElementSelector(element: Element): string {
  // Use ID if available
  if (element.id) {
    return `#${element.id}`;
  }
  
  let selector = element.tagName.toLowerCase();
  
  // Add classes (limit to first 3)
  if (element.className && typeof element.className === 'string') {
    const classes = element.className.trim().split(/\s+/).slice(0, 3);
    if (classes.length > 0 && classes[0]) {
      selector += '.' + classes.join('.');
    }
  }
  
  // Add position among siblings for more uniqueness
  const parent = element.parentElement;
  if (parent) {
    const siblings = Array.from(parent.children).filter(child => child.tagName === element.tagName);
    if (siblings.length > 1) {
      const index = siblings.indexOf(element);
      if (index !== -1) {
        selector += `:nth-of-type(${index + 1})`;
      }
    }
  }
  
  return selector;
}

/**
 * Elements that should not trigger dead click detection
 */
const DEAD_CLICK_EXCLUDE_SELECTOR =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="range"]),' +
  'textarea,' +
  'select,' +
  '[contenteditable],' +
  '[contenteditable] *,' +
  'canvas,' +
  'a[href],' +
  'a[href] *';

/**
 * Check if element should be excluded from dead click detection
 */
export function isElementExcludedFromDeadClick(element: Element): boolean {
  try {
    // Check the element itself
    if (element.matches(DEAD_CLICK_EXCLUDE_SELECTOR)) {
      return true;
    }
    
    // Check if it's a child of an excluded element
    if (element.closest(DEAD_CLICK_EXCLUDE_SELECTOR)) {
      return true;
    }
    
    // Special case: check if it's a label pointing to an input
    if (element.tagName === 'LABEL') {
      const labelElement = element as HTMLLabelElement;
      if (labelElement.htmlFor) {
        const targetInput = document.getElementById(labelElement.htmlFor);
        if (targetInput) {
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    // If selector matching fails, assume it's not excluded
    return false;
  }
}

/**
 * Check if there's currently text selection
 */
export function hasTextSelection(): boolean {
  const selection = window.getSelection();
  return selection ? selection.toString().length > 0 : false;
}

